/**
 * services/platform/platformApi.ts
 * The integration layer between platform screens and skplatform-api. Screens
 * never build URLs or call axios directly — they call these functions, which
 * read endpoints from api-endpoints/ and go through the shared axios instance
 * (httpOnly cookie session + silent refresh).
 *
 * Each function returns the unwrapped `data` payload. Screens wrap calls in
 * try/catch and fall back to the seeded working copy when a route isn't live
 * yet (the console stays fully navigable in dev).
 */

import axiosInstance from '../../api-endpoints/axiosInstance';
import * as EP from '../../api-endpoints/api-endpoints';
import type {
  ApiEnvelope, Org, UpdateOrgPayload, OrgMember, OrgInvitation, CreateInvitationPayload,
  InvoicePage, MfaEnrollResponse,
  Capability, Connection, ConnectionProvider, BindingHistoryRow, HqOverview, HqActivity, TransferRequest,
  CatalogProduct, CatalogPlan, CatalogBundle, EmbedRegistration, Adjustment, AdjustmentInput,
  RecoveryCodesResponse, AuditEvent, AdminOrgSummary, OrgHost, OutboxDeadLetter,
  ResidencyZone, SigningKeyRow, RlsBypassRow, RlsBypassSiteCount,
  RealmMismatchRow, AppAudienceRow, CustomerActivityPeriodRow, CustomerAccessRow,
} from '../../schema/platform/types';

const unwrap = <T,>(d: any): T => (d && typeof d === 'object' && 'data' in d ? d.data : d);

/* ── Organization ── */
export async function getOrg(orgId: string): Promise<Org> {
  const { data } = await axiosInstance.get<ApiEnvelope<Org>>(EP.Platform_Org_Url(orgId));
  return unwrap<Org>(data);
}
export async function updateOrg(orgId: string, payload: UpdateOrgPayload): Promise<Org> {
  const { data } = await axiosInstance.patch<ApiEnvelope<Org>>(EP.Platform_Org_Url(orgId), payload);
  return unwrap<Org>(data);
}

/* ── Team & invitations ── */
export async function listMembers(orgId: string): Promise<OrgMember[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<OrgMember[]>>(EP.Platform_Org_Members_Url(orgId));
  return unwrap<OrgMember[]>(data);
}
export async function grantRole(orgId: string, userId: string, roleId: string) {
  const { data } = await axiosInstance.post(EP.Platform_Org_Member_Roles_Url(orgId, userId), { role_id: roleId });
  return unwrap(data);
}
export async function removeMember(orgId: string, userId: string) {
  await axiosInstance.delete(EP.Platform_Org_Member_Url(orgId, userId));
}
export async function listInvitations(orgId: string): Promise<OrgInvitation[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<OrgInvitation[]>>(EP.Platform_Org_Invitations_Url(orgId));
  return unwrap<OrgInvitation[]>(data);
}
export async function createInvitation(orgId: string, payload: CreateInvitationPayload): Promise<OrgInvitation> {
  const { data } = await axiosInstance.post<ApiEnvelope<OrgInvitation>>(EP.Platform_Org_Invitations_Url(orgId), payload);
  return unwrap<OrgInvitation>(data);
}
export async function resendInvitation(orgId: string, invId: string) {
  await axiosInstance.post(EP.Platform_Org_Invitation_Resend_Url(orgId, invId));
}
export async function cancelInvitation(orgId: string, invId: string) {
  await axiosInstance.delete(EP.Platform_Org_Invitation_Url(orgId, invId));
}

/* ── Embedded plan card + drawer ── */
/** Short-lived (5 min) token the card/drawer iframes hold in memory (CC-04, CONTRACT R10). */
export async function createEmbedToken(productCode: string): Promise<string> {
  const { data } = await axiosInstance.post<ApiEnvelope<{ token: string }> | { token: string }>(
    EP.Platform_Embed_Tokens_Url,
    { productCode },
  );
  return unwrap<{ token: string }>(data).token;
}

/* ── Billing ── */
export async function listInvoices(orgId: string, cursor?: string): Promise<InvoicePage> {
  const { data } = await axiosInstance.get<InvoicePage>(EP.Platform_Org_Invoices_Url(orgId), {
    params: cursor ? { cursor } : undefined,
  });
  return data;
}

/* ── Connections (CR-MTR-PLT-001) ── */
export async function listConnectionProviders(): Promise<ConnectionProvider[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<ConnectionProvider[]>>(EP.Platform_Connection_Providers_Url);
  return unwrap<ConnectionProvider[]>(data) ?? [];
}
export async function listConnections(orgId: string): Promise<Connection[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<Connection[]>>(EP.Platform_Org_Connections_Url(orgId));
  return unwrap<Connection[]>(data) ?? [];
}
/** Creating one REQUIRES ≥1 product; one binding per product. A provider that needs its own setup returns onboardingUrl. */
export async function createConnection(
  orgId: string,
  input: { capability: Capability; provider: string; displayName: string; productCodes: string[] },
): Promise<{ connection: Connection; onboardingUrl: string | null }> {
  const { data } = await axiosInstance.post(EP.Platform_Org_Connections_Url(orgId), input);
  return unwrap(data);
}
export async function bindConnection(connectionId: string, orgId: string, productCode: string): Promise<void> {
  await axiosInstance.post(EP.Platform_Connection_Bindings_Url(connectionId), { orgId, productCode });
}
export async function unbindConnection(connectionId: string, bindingId: string): Promise<void> {
  await axiosInstance.delete(EP.Platform_Connection_Binding_Url(connectionId, bindingId));
}
export async function setBindingCap(connectionId: string, bindingId: string, monthlyCap: number | null): Promise<void> {
  await axiosInstance.put(EP.Platform_Connection_Binding_Cap_Url(connectionId, bindingId), { monthlyCap });
}
export async function connectionHistory(connectionId: string): Promise<BindingHistoryRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<BindingHistoryRow[]>>(EP.Platform_Connection_History_Url(connectionId));
  return unwrap<BindingHistoryRow[]>(data) ?? [];
}
export async function deactivateConnection(connectionId: string): Promise<void> {
  await axiosInstance.post(EP.Platform_Connection_Deactivate_Url(connectionId));
}

/* ── HQ console (CR-MTR-CON-001) ── */
export async function getHqOverview(orgId: string): Promise<HqOverview> {
  const { data } = await axiosInstance.get<ApiEnvelope<HqOverview>>(EP.Billing_Hq_Outlets_Url(orgId));
  return unwrap<HqOverview>(data);
}
export async function getHqActivity(orgId: string): Promise<HqActivity[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<HqActivity[]>>(EP.Billing_Hq_Activity_Url(orgId));
  return unwrap<HqActivity[]>(data) ?? [];
}
export async function createTransfer(input: TransferRequest): Promise<void> {
  await axiosInstance.post(EP.Billing_Transfers_Url, input);
}

/* ── Staff: plan catalog (CC-01 §7) ── */
export async function catalogProducts(): Promise<CatalogProduct[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<CatalogProduct[]>>(EP.Admin_Catalog_Products_Url);
  return unwrap<CatalogProduct[]>(data) ?? [];
}
export async function saveCatalogProduct(p: CatalogProduct, isNew: boolean): Promise<void> {
  if (isNew) await axiosInstance.post(EP.Admin_Catalog_Products_Url, p);
  else await axiosInstance.patch(EP.Admin_Catalog_Product_Url(p.code), p);
}
export async function catalogPlans(): Promise<CatalogPlan[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<CatalogPlan[]>>(EP.Admin_Catalog_Plans_Url);
  return unwrap<CatalogPlan[]>(data) ?? [];
}
export async function saveCatalogPlan(p: CatalogPlan): Promise<void> {
  if (p.id) await axiosInstance.patch(EP.Admin_Catalog_Plan_Url(p.id), p);
  else await axiosInstance.post(EP.Admin_Catalog_Plans_Url, p);
}
export async function catalogBundles(): Promise<CatalogBundle[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<CatalogBundle[]>>(EP.Admin_Catalog_Bundles_Url);
  return unwrap<CatalogBundle[]>(data) ?? [];
}
export async function saveCatalogBundle(b: CatalogBundle): Promise<void> {
  if (b.id) await axiosInstance.patch(EP.Admin_Catalog_Bundle_Url(b.id), b);
  else await axiosInstance.post(EP.Admin_Catalog_Bundles_Url, b);
}
export async function embedRegistrations(): Promise<EmbedRegistration[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<EmbedRegistration[]>>(EP.Admin_Catalog_Embeds_Url);
  return unwrap<EmbedRegistration[]>(data) ?? [];
}
export async function saveEmbedRegistration(r: EmbedRegistration): Promise<void> {
  if (r.id) await axiosInstance.patch(EP.Admin_Catalog_Embed_Url(r.id), r);
  else await axiosInstance.post(EP.Admin_Catalog_Embeds_Url, r);
}

/* ── Staff: adjustments (BIL-001 §9) ── */
export async function listAdjustments(status?: Adjustment['status']): Promise<Adjustment[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<Adjustment[]>>(EP.Admin_Adjustments_Url, {
    params: status ? { status } : undefined,
  });
  return unwrap<Adjustment[]>(data) ?? [];
}
export async function createAdjustment(input: AdjustmentInput): Promise<Adjustment> {
  const { data } = await axiosInstance.post<ApiEnvelope<Adjustment>>(EP.Admin_Adjustments_Url, input);
  return unwrap<Adjustment>(data);
}
export async function approveAdjustment(id: string): Promise<void> {
  await axiosInstance.post(EP.Admin_Adjustment_Approve_Url(id));
}
export async function rejectAdjustment(id: string, reason?: string): Promise<void> {
  await axiosInstance.post(EP.Admin_Adjustment_Reject_Url(id), reason ? { reason } : {});
}

/* ── Security (MFA) ── */
export async function enrollMfa(): Promise<MfaEnrollResponse> {
  const { data } = await axiosInstance.post<ApiEnvelope<MfaEnrollResponse>>(EP.Platform_Mfa_Enroll_Url);
  return unwrap<MfaEnrollResponse>(data);
}
export async function verifyMfaEnrollment(code: string): Promise<RecoveryCodesResponse> {
  const { data } = await axiosInstance.post<ApiEnvelope<RecoveryCodesResponse>>(EP.Platform_Mfa_Verify_Enroll_Url, { code });
  return unwrap<RecoveryCodesResponse>(data);
}
export async function disableMfa(code: string) {
  await axiosInstance.post(EP.Platform_Mfa_Disable_Url, { code });
}

/* ── Audit ── */
export async function getAuditLog(params?: Record<string, string>): Promise<AuditEvent[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<AuditEvent[]>>(EP.Platform_Audit_Log_Url, { params });
  return unwrap<AuditEvent[]>(data);
}

/* ── Admin ── */
export async function adminListOrgs(query?: string): Promise<AdminOrgSummary[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<AdminOrgSummary[]>>(EP.Platform_Admin_Orgs_Url, { params: query ? { query } : undefined });
  return unwrap<AdminOrgSummary[]>(data);
}

/* ── Admin — flagged-gap stubs (routes to be confirmed; see api-endpoints) ── */
export async function adminListApplications(): Promise<any[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<any[]>>(EP.Platform_Admin_Applications_Url);
  return unwrap<any[]>(data);
}

/* ── Domains (CH-U1) ──────────────────────────────────────────────────────
   `listHosts` defaults to ACTIVE only. History is opt-in, matching the API and
   matching CH-U1's default filter — a panel that opened showing every domain
   the org ever held would bury the ones actually routing traffic. */
export async function listOrgHosts(orgId: string, includeInactive = false): Promise<OrgHost[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<OrgHost[]>>(
    EP.Platform_Org_Hosts_Url(orgId),
    includeInactive ? { params: { include_inactive: 'true' } } : undefined,
  );
  return unwrap<OrgHost[]>(data);
}

export async function bindOrgHost(orgId: string, host: string): Promise<OrgHost> {
  const { data } = await axiosInstance.post<ApiEnvelope<OrgHost>>(
    EP.Platform_Org_Hosts_Url(orgId),
    { host },
  );
  return unwrap<OrgHost>(data);
}

/**
 * Unbind a domain. DELETE is the verb; the effect is a DEACTIVATION — the row
 * survives and still appears under `include_inactive`. Nothing here removes a
 * row, which is why the UI says "Deactivate" and offers no delete.
 */
export async function deactivateOrgHost(orgId: string, hostId: string): Promise<void> {
  await axiosInstance.delete(EP.Platform_Org_Host_Url(orgId, hostId));
}

/* ── Outbox dead letters (CH-U5) ─────────────────────────────────────────── */
export async function listOutboxDeadLetters(limit = 50): Promise<OutboxDeadLetter[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<OutboxDeadLetter[]>>(
    EP.Platform_Outbox_Dlq_Url,
    { params: { limit } },
  );
  return unwrap<OutboxDeadLetter[]>(data);
}

export async function requeueOutboxEvent(id: string): Promise<void> {
  await axiosInstance.post(EP.Platform_Outbox_Requeue_Url(id));
}

/* ── SKPLATFORM-CC-006 / UICC-006 — residency, keys, ledgers, customer access ──
   Every function returns the unwrapped payload and lets the caller decide what
   an empty or failed read means. The two ledger reads in particular are
   EXPECTED to come back empty until the reporting role exists (see the note in
   api-endpoints.tsx), so the screens treat empty as a state rather than as an
   error. */

export async function listResidencyZones(): Promise<ResidencyZone[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<ResidencyZone[]>>(
    EP.Platform_Residency_Zones_Url,
  );
  return unwrap<ResidencyZone[]>(data) ?? [];
}

export async function listSigningKeys(): Promise<SigningKeyRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<SigningKeyRow[]>>(
    EP.Platform_Admin_Signing_Keys_Url,
  );
  return unwrap<SigningKeyRow[]>(data) ?? [];
}

export async function listRlsBypass(limit = 200): Promise<RlsBypassRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<RlsBypassRow[]>>(
    `${EP.Platform_Admin_Rls_Bypass_Url}?limit=${limit}`,
  );
  return unwrap<RlsBypassRow[]>(data) ?? [];
}

export async function listRlsBypassSites(): Promise<RlsBypassSiteCount[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<RlsBypassSiteCount[]>>(
    EP.Platform_Admin_Rls_Bypass_Sites_Url,
  );
  return unwrap<RlsBypassSiteCount[]>(data) ?? [];
}

export async function listRealmMismatches(sinceHours = 24): Promise<RealmMismatchRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<RealmMismatchRow[]>>(
    `${EP.Platform_Admin_Realm_Mismatch_Url}?since_hours=${sinceHours}`,
  );
  return unwrap<RealmMismatchRow[]>(data) ?? [];
}

export async function listAppAudiences(): Promise<AppAudienceRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<AppAudienceRow[]>>(
    EP.Platform_Admin_Audiences_Url,
  );
  return unwrap<AppAudienceRow[]>(data) ?? [];
}

export async function listCustomerActivityAcrossTenants(): Promise<CustomerActivityPeriodRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<CustomerActivityPeriodRow[]>>(
    EP.Platform_Admin_Customer_Activity_Url,
  );
  return unwrap<CustomerActivityPeriodRow[]>(data) ?? [];
}

export async function getCustomerActivity(orgId: string): Promise<CustomerActivityPeriodRow> {
  const { data } = await axiosInstance.get<ApiEnvelope<CustomerActivityPeriodRow>>(
    EP.Platform_Org_Customer_Activity_Url(orgId),
  );
  return unwrap<CustomerActivityPeriodRow>(data);
}

export async function listCustomerAccess(orgId: string): Promise<CustomerAccessRow[]> {
  return (await listCustomerAccessWithMeta(orgId)).rows;
}

/**
 * The access list, WITH the server's reason when it is unavailable.
 *
 * Platform holds no customer identifier and cannot enumerate customer subjects
 * — that absence is the property keeping customers off the seat count, not a
 * gap. So the endpoint answers with an empty list and a reason, and the screen
 * needs the reason: "no customers have signed in" and "this console cannot see
 * the list" are different facts, and on an access surface they matter
 * differently.
 */
export async function listCustomerAccessWithMeta(
  orgId: string,
): Promise<{ rows: CustomerAccessRow[]; reason: string | null }> {
  const { data } = await axiosInstance.get<
    ApiEnvelope<CustomerAccessRow[]> & { meta?: { available?: boolean; reason?: string } }
  >(`${EP.Platform_Org_Customer_Access_Url(orgId)}?sort=last_seen`);
  const rows = unwrap<CustomerAccessRow[]>(data) ?? [];
  const meta = (data as { meta?: { available?: boolean; reason?: string } }).meta;
  return { rows, reason: meta?.available === false ? (meta.reason ?? 'unavailable') : null };
}

/**
 * Bulk revoke. Revoking access does NOT delete the customer's records and does
 * not affect their access at any other business — the confirmation dialog says
 * both, and it says them because an operator revoking 900 dormant grants must
 * be certain they have not destroyed 900 booking histories.
 */
export async function revokeCustomerAccess(orgId: string, ids: string[]): Promise<{ revoked: number }> {
  const { data } = await axiosInstance.post<ApiEnvelope<{ revoked: number }>>(
    EP.Platform_Org_Customer_Access_Revoke_Url(orgId),
    { customer_ids: ids },
  );
  return unwrap<{ revoked: number }>(data);
}
